/**
 * The PURRZ redesign's behaviour layer: the per-item scroll reveal, the collection pages' band gate
 * and pop, the rail edge signal, the compact card's name line count and the per-band module loader.
 *
 * It is an ES module, loaded once per redesign template by `layout/theme.liquid` as
 * `<script type="module">`, so it is deferred by definition and adds nothing to the parser's
 * critical path. It declares no global: the auto-start at the bottom of this file is what wires
 * the page, and a band with behaviour of its own is a separate module this file imports on demand
 * (`data-pz-module`), handed the shared helpers through a frozen `api` object.
 *
 * NOTHING HERE MAY BE LOAD-BEARING FOR CONTENT. The reveal's hidden states in `purrz-design.css`
 * apply only under `:root.pz-reveal-on`, and this module adds that class itself, and only when it
 * is running, has an IntersectionObserver, the reader has not asked for reduced motion and the
 * Theme Editor is closed. So JavaScript switched off, a 404 on this file, a Content-Security-Policy
 * refusal, reduced motion and the editor all leave every item painted at rest; the failsafe timer
 * below covers the one failure left once the page is armed, an observer that is constructed and
 * then never calls back, and the focus reveal shows any item keyboard focus enters before its
 * observer does. `data-pz-reveal="none"` opts an item out at render time.
 *
 * THE COLLECTION PAGES, AS DRAWN (fidelity fix FX0, 2026-10-03: the drawing is the spec). On a
 * page that holds a collection band, the item reveal takes the drawings' values (60ms steps, a .7s
 * rise, an item already within 92% of the screen left alone, no side slides) and `initGate` holds
 * every band below the hero 48px low until the first scroll, as the drawings' `gate()` does. The
 * gate is armed exactly like the reveal (`pz-gate-on`), so it hides nothing without JavaScript,
 * under reduced motion or in the Theme Editor. `pop` is the drawings' count and badge pop, handed
 * to band modules as `api.pop`.
 *
 * Unit tests: `tests/design/reveal.test.ts` (reveal, gate, pop, rails, card text) and
 * `tests/design/modules.test.ts` (the loader and the auto-start).
 */

/* ---- Reveal --------------------------------------------------------------------------------- */

/** The handoff's IntersectionObserver threshold and bottom margin for an item reveal. */
export const REVEAL_THRESHOLD = 0.12;
export const REVEAL_ROOT_MARGIN = '0px 0px -6% 0px';

/** Items entering in one callback start 80ms apart: 0, 80, ... 400, then 480 for the 7th on. */
export const REVEAL_STAGGER_MS = 80;
export const REVEAL_STAGGER_CAP = 6;

/** The item transition (`--pz-duration-reveal` in the tokens); the clean-up waits this long. */
export const REVEAL_DURATION_MS = 750;

/**
 * `data-pz-reveal="enter"`. Twelve per cent of a block TALLER than the viewport is a long way in:
 * a stack 2,000px high on a phone would sit blank until 240px of it had scrolled past, which reads
 * as extra section spacing rather than as an entrance. The enter rule reveals such a block once its
 * first `REVEAL_ENTER_PX` are on screen. The floor keeps a real rung for a block so tall that 80px
 * would round away to nothing.
 */
export const REVEAL_ENTER_PX = 80;
export const REVEAL_ENTER_MIN_THRESHOLD = 0.01;

/**
 * The share of the viewport the reveal observers see: `REVEAL_ROOT_MARGIN` takes 6% off the
 * bottom. A block taller than this can never pass the shared 12% (an 8,518px policy band on a
 * phone would need 1,022px of a screen that shows 793), so it is revealed by the enter rule.
 */
const REVEAL_VISIBLE_SHARE = 0.94;

/**
 * The failsafe deadline: how long an observer's items wait for it to say ANYTHING.
 *
 * It answers one failure - an IntersectionObserver that is constructed and then never delivers a
 * callback, which is what a replaced or broken implementation (an overlay, an extension) looks
 * like from here. The FIRST callback cancels it, intersecting or not, because the specification
 * guarantees an initial entry for every observed target: a timer that waited for intersection
 * instead would reveal every item below the fold 2.5 seconds after load, each playing its
 * entrance off screen and sitting at rest by the time the shopper arrived.
 */
export const REVEAL_FAILSAFE_MS = 2500;

/** The phone fan (`data-pz-reveal="fan"`): its own threshold, a 150ms beat, a 1050ms unfold. */
export const FAN_THRESHOLD = 0.3;
export const FAN_DELAY_MS = 150;
export const FAN_DURATION_MS = 1050;

/** A split row slides its two children in from their sides only when it is this wide... */
export const SPLIT_MIN_WIDTH = 700;
/** ...and only when their tops sit within this many px of each other (one visual row). */
export const SPLIT_ROW_TOLERANCE = 40;

export const REVEAL_SELECTOR = '[data-pz-reveal]';
export const REVEAL_ATTRIBUTE = 'data-pz-reveal';
export const REVEAL_CLASS = 'pz-in';
export const REVEAL_ACTIVE_CLASS = 'pz-revealing';
export const REVEAL_ARMED_CLASS = 'pz-reveal-on';

/**
 * `data-pz-reveal="none"`: the item is painted by the stylesheet and never observed, so a section
 * can keep the one hook and still opt an item out at render time.
 */
export const REVEAL_OPT_OUT = 'none';
export const REVEAL_FAN = 'fan';
export const REVEAL_ENTER = 'enter';
export const REVEAL_SPLIT_SELECTOR = '[data-pz-reveal-split]';
export const REVEAL_FROM_ATTRIBUTE = 'data-pz-reveal-from';

/** The custom property the stylesheet's reveal transition reads as its delay. */
const REVEAL_DELAY_PROPERTY = '--pz-reveal-delay';

/** How long after its transition ends an item keeps `pz-revealing`, for a late frame. */
const REVEAL_SETTLE_MS = 100;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/* ---- The collection page ------------------------------------------------------------------- */

/**
 * A collection page holds a band whose class names a `pz-collection-*` root: every collection
 * section carries one, and only `collection` templates enable them.
 */
export const COLLECTION_BAND_SELECTOR = ".pz-band[class*='pz-collection-']";

/**
 * Whether `doc` is a collection page.
 *
 * @param {Document|null} [doc]
 * @returns {boolean}
 */
export const isCollectionPage = (doc = globalThis.document) =>
  Boolean(
    doc && typeof doc.querySelector === 'function' && doc.querySelector(COLLECTION_BAND_SELECTOR),
  );

/**
 * The collection drawings' item reveal (their `reveal()`): entries of one callback 60ms apart, at
 * most six steps, the .7s rise (`--pz-duration-reveal-rise`; the fade is .6s), and an item whose
 * top is already within 92% of the viewport left at rest. `order: 'source'`: the steps follow the
 * order the observer delivers the entries in, which is the order the items were observed in,
 * document order, as the drawings' `reveal()` counts them; not top to bottom, then left to right.
 * The two differ only where an item sits higher than one before it in the document and both can
 * arrive in one callback: the Ask A Vet band's five screens (the centre, home, stands highest and
 * is the drawing's third; fidelity loop 1, lower GAP 2), its floats against its heading, and at
 * 1280px the FAQ heading against the first question beside it. The build's document order is the
 * drawing's in each, so each steps as drawn.
 */
export const COLLECTION_REVEAL = Object.freeze({
  step: 60,
  cap: 6,
  duration: 700,
  onScreen: 0.92,
  order: 'source',
});

const DEFAULT_REVEAL = Object.freeze({
  step: REVEAL_STAGGER_MS,
  cap: REVEAL_STAGGER_CAP,
  duration: REVEAL_DURATION_MS,
  onScreen: 1,
  order: 'position',
});

/**
 * The reveal's numbers for a page.
 *
 * @param {boolean} collection Whether the page is a collection page.
 * @returns {{ step: number, cap: number, duration: number, onScreen: number, order: string }}
 */
export const revealProfile = (collection) => (collection ? COLLECTION_REVEAL : DEFAULT_REVEAL);

/*
 * Element sets rather than a flag on the element: `shopify:section:load` re-runs the initialisers
 * over a section the Theme Editor has just replaced, and an item that is still on the page must
 * not be observed or bound a second time.
 */
const revealed = new WeakSet();
const railsBound = new WeakSet();

/**
 * Items an observer is still waiting on, each with the call that ends the wait (marks it done and
 * unobserves it). The focus reveal shows an item before its observer reaches it, and must leave
 * neither that observer nor the failsafe anything to do for it afterwards.
 */
const revealWaits = new WeakMap();

/** The window, or null off a browser. Timers are reached through it so this file needs no global. */
const view = () => (typeof window === 'undefined' ? null : window);

/** `setTimeout` through the window; null off a browser. Handed to band modules as `api.later`. */
const later = (callback, milliseconds) => {
  const scope = view();
  if (!scope || typeof scope.setTimeout !== 'function') return null;
  return scope.setTimeout(callback, milliseconds);
};

/** `clearTimeout` through the window; a null handle is ignored. Handed on as `api.cancel`. */
const cancel = (handle) => {
  const scope = view();
  if (handle === null || handle === undefined || !scope) return;
  if (typeof scope.clearTimeout !== 'function') return;
  scope.clearTimeout(handle);
};

/** Taller than the part of the viewport the observers see (never, when the height is unknown). */
const tallerThanView = (height, viewportHeight) =>
  viewportHeight > 0 && height > viewportHeight * REVEAL_VISIBLE_SHARE;

/**
 * The threshold an item is observed at.
 *
 * @param {string} rule The value of `data-pz-reveal`: `fan`, `enter`, or anything else.
 * @param {number} height The item's laid-out height in pixels (used by the enter rule only).
 * @param {number} [viewportHeight] The viewport's height. Any block but a fan that is taller than
 *   the part of it the observers see takes the enter rule, because the shared threshold would be
 *   out of its reach and it would stay hidden. Unknown (0) changes nothing.
 * @returns {number}
 */
export const revealThreshold = (rule, height, viewportHeight = 0) => {
  if (rule === REVEAL_FAN) return FAN_THRESHOLD;
  const enter = rule === REVEAL_ENTER || tallerThanView(height, viewportHeight);
  if (!enter || !(height > 0)) return REVEAL_THRESHOLD;
  return Math.max(REVEAL_ENTER_MIN_THRESHOLD, Math.min(REVEAL_THRESHOLD, REVEAL_ENTER_PX / height));
};

/**
 * Transition delays for items that intersect in the same callback: top to bottom, then left to
 * right, `step` ms apart, never more than `cap` steps late.
 *
 * @param {{ top: number, left: number }[]} rects In the order the entries arrived.
 * @param {number} [step]
 * @param {number} [cap]
 * @returns {number[]} One delay in ms per rect, in the same order.
 */
export const staggerDelays = (rects, step = REVEAL_STAGGER_MS, cap = REVEAL_STAGGER_CAP) => {
  const order = rects
    .map((rect, index) => ({ rect, index }))
    .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left);
  const delays = new Array(rects.length).fill(0);
  order.forEach(({ index }, position) => {
    delays[index] = Math.min(position, cap) * step;
  });
  return delays;
};

/**
 * The side each child of a split row slides in from, by where it SITS (a CSS `order` swap is
 * honoured), or null when the row is too narrow or its two children do not share one row.
 *
 * @param {number} containerWidth
 * @param {{ top: number, left: number }[]} rects The two children, in DOM order.
 * @returns {null|['left'|'right', 'left'|'right']}
 */
export const splitSides = (containerWidth, rects) => {
  if (rects.length !== 2 || !(containerWidth > SPLIT_MIN_WIDTH)) return null;
  if (!(Math.abs(rects[0].top - rects[1].top) < SPLIT_ROW_TOLERANCE)) return null;
  return rects[0].left <= rects[1].left ? ['left', 'right'] : ['right', 'left'];
};

/**
 * Whether the module may hide anything at all.
 *
 * @param {{ hasObserver: boolean, reducedMotion: boolean, designMode: boolean }} conditions
 * @returns {boolean}
 */
export const shouldArmReveal = ({ hasObserver, reducedMotion, designMode }) =>
  Boolean(hasObserver) && !reducedMotion && !designMode;

/** @returns {boolean} Whether the reader has asked the system for reduced motion. */
export const prefersReducedMotion = () =>
  typeof matchMedia === 'function' && Boolean(matchMedia(REDUCED_MOTION_QUERY)?.matches);

/** @returns {boolean} Whether the page is open inside the Shopify Theme Editor. */
export const isDesignMode = () => Boolean(view()?.Shopify?.designMode);

const elementsUnder = (root, selector) => {
  if (!root || typeof root.querySelectorAll !== 'function') return null;
  const found = [...root.querySelectorAll(selector)];
  if (typeof root.matches === 'function' && root.matches(selector)) found.unshift(root);
  return found;
};

const ruleOf = (block) => block.getAttribute(REVEAL_ATTRIBUTE) ?? '';

const rectOf = (element) =>
  typeof element.getBoundingClientRect === 'function' ? element.getBoundingClientRect() : null;

/**
 * Painted inside the viewport right now (the item was visible before this module ran): its top
 * within `share` of the viewport, its bottom below the top edge.
 */
const onScreen = (block, viewportHeight, share = 1) => {
  const rect = rectOf(block);
  return Boolean(rect) && rect.top < viewportHeight * share && rect.bottom > 0;
};

/**
 * The side each of the two children of every split row that sits side by side slides in from, as
 * `[child, side]` pairs. It only reads: `initReveal` writes them as `data-pz-reveal-from` once
 * every block is measured (PERF-07, PURRZ performance plan 2026-10-28).
 */
const splitRowSides = (root, fresh) => {
  const rows = elementsUnder(root, REVEAL_SPLIT_SELECTOR) ?? [];
  return rows.flatMap((row) => {
    const children = [...(row.children ?? [])].filter(
      (child) =>
        typeof child.getAttribute === 'function' &&
        ['', 'item'].includes(child.getAttribute(REVEAL_ATTRIBUTE) ?? REVEAL_OPT_OUT) &&
        fresh.has(child),
    );
    const rects = children.map(rectOf);
    if (rects.some((rect) => rect === null)) return [];
    const sides = splitSides(row.clientWidth, rects);
    if (sides === null) return [];
    return children.map((child, index) => [child, sides[index]]);
  });
};

/**
 * Watch `blocks` with one IntersectionObserver and reveal each as it enters: every entry of one
 * callback gets its stagger delay, `pz-revealing` (the transition) and `pz-in` (the rest state),
 * is unobserved, and loses `pz-revealing` and its delay again once the transition is over, so the
 * item's own hover transitions apply afterwards.
 */
const observeBlocks = (blocks, options, { fan = false, profile = DEFAULT_REVEAL } = {}) => {
  const done = new WeakSet();
  let failsafe = null;

  const settleLater = (block, delay) => {
    const wait = fan
      ? FAN_DELAY_MS + FAN_DURATION_MS + REVEAL_SETTLE_MS
      : profile.duration + delay + REVEAL_SETTLE_MS;
    later(() => {
      block.classList.remove(REVEAL_ACTIVE_CLASS);
      block.style?.removeProperty?.(REVEAL_DELAY_PROPERTY);
    }, wait);
  };

  const observer = new IntersectionObserver((entries) => {
    /*
     * ANY callback retires the failsafe, intersecting or not: the specification delivers an
     * initial entry for every target the moment it is observed, so one call is proof the observer
     * is alive and the timer has nothing left to cover.
     */
    cancel(failsafe);
    failsafe = null;

    const intersecting = entries.filter((entry) => entry.isIntersecting && !done.has(entry.target));
    /* Revealed already (for print, by `revealAll`): at rest, so no second entrance. */
    intersecting
      .filter((entry) => entry.target.classList.contains(REVEAL_CLASS))
      .forEach((entry) => {
        done.add(entry.target);
        revealWaits.delete(entry.target);
        observer.unobserve(entry.target);
      });
    const entering = intersecting.filter((entry) => !done.has(entry.target));
    let delays;
    if (fan) delays = entering.map(() => 0);
    else if (profile.order === 'source')
      delays = entering.map((_, index) => Math.min(index, profile.cap) * profile.step);
    else
      delays = staggerDelays(
        entering.map((entry) => entry.boundingClientRect ?? rectOf(entry.target)),
        profile.step,
        profile.cap,
      );

    entering.forEach((entry, index) => {
      const block = entry.target;
      const delay = delays[index];
      done.add(block);
      revealWaits.delete(block);
      if (!fan) block.style?.setProperty?.(REVEAL_DELAY_PROPERTY, `${delay}ms`);
      block.classList.add(REVEAL_ACTIVE_CLASS);
      block.classList.add(REVEAL_CLASS);
      observer.unobserve(block);
      settleLater(block, delay);
    });
  }, options);

  failsafe = later(() => {
    failsafe = null;
    blocks.forEach((block) => {
      if (done.has(block)) return;
      done.add(block);
      revealWaits.delete(block);
      block.classList.add(REVEAL_CLASS);
    });
    observer.disconnect();
  }, REVEAL_FAILSAFE_MS);

  blocks.forEach((block) => {
    observer.observe(block);
    revealWaits.set(block, () => {
      done.add(block);
      observer.unobserve(block);
    });
  });
};

/**
 * Show every `[data-pz-reveal]` item under `root` (root included) at rest, at once: `pz-in`
 * without `pz-revealing`, so nothing transitions. An opt-out and an item already in are left
 * alone. It is what the page runs on `beforeprint`, so a page printed (or saved as PDF) before it
 * was scrolled prints every item, and every phone fan unfolded, instead of blank space.
 *
 * @param {ParentNode|null} [root]
 * @returns {number} How many items it revealed.
 */
export const revealAll = (root = globalThis.document) => {
  const blocks = elementsUnder(root, REVEAL_SELECTOR) ?? [];
  let shown = 0;
  blocks.forEach((block) => {
    if (ruleOf(block) === REVEAL_OPT_OUT || block.classList.contains(REVEAL_CLASS)) return;
    block.classList.add(REVEAL_CLASS);
    shown += 1;
  });
  (elementsUnder(root, GATE_SELECTOR) ?? []).forEach((band) => {
    if (releaseGated(band)) shown += 1;
  });
  return shown;
};

/** The window the `beforeprint` listener is on, so it is registered once per page. */
let printScope = null;

/** Once the page arms, reveal everything it holds before it prints. */
const listenForPrint = (doc) => {
  const scope = view();
  if (!scope || scope === printScope || typeof scope.addEventListener !== 'function') return;
  scope.addEventListener('beforeprint', () => revealAll(doc));
  printScope = scope;
};

/**
 * Show, at once and at rest, every reveal item that holds `target` and is not in yet: `pz-in`
 * without `pz-revealing` (no fade under the focus ring), and its observer stops waiting on it. A
 * fan never fades and an opt-out is never hidden, so both are left alone; so is an item already
 * in, mid-transition or at rest. The walk goes to the top, because an item may sit inside another
 * revealed wrapper (a card in a revealed row), and the walk must not stop at the first. A gated
 * collection band on the way is shown at rest too (`pz-gate-in` without `pz-gating`).
 *
 * It is what the page runs on `focusin`. Tab scrolls a control only to the NEAREST edge, which
 * can leave its item inside the bottom 6% the observers do not see, or short of their 12%
 * threshold; the observer then never fires, and the focused control and its ring sat at opacity 0
 * until the shopper scrolled (WCAG 2.4.7).
 *
 * @param {EventTarget|null} target The element focus arrived on.
 * @returns {number} How many items it showed.
 */
export const revealFocused = (target) => {
  let shown = 0;
  for (let node = target; node; node = node.parentElement ?? null) {
    const rule =
      typeof node.getAttribute === 'function' ? node.getAttribute(REVEAL_ATTRIBUTE) : null;
    const hides = rule !== null && rule !== REVEAL_OPT_OUT && rule !== REVEAL_FAN;
    if (hides && !node.classList.contains(REVEAL_CLASS)) {
      node.classList.add(REVEAL_CLASS);
      const stopWaiting = revealWaits.get(node);
      revealWaits.delete(node);
      stopWaiting?.();
      shown += 1;
    }
    if (releaseGated(node)) shown += 1;
  }
  return shown;
};

/** The document the `focusin` listener is on, so it is registered once per page. */
let focusScope = null;

/**
 * Once the page arms, show any item focus enters. One listener on the document serves every item
 * and every section the Theme Editor re-renders, so an unloaded section leaves nothing behind; the
 * capture phase, because `focusin` is the one event here a band module could stop.
 */
const listenForFocus = (doc) => {
  if (!doc || doc === focusScope || typeof doc.addEventListener !== 'function') return;
  doc.addEventListener('focusin', (event) => revealFocused(event?.target ?? null), true);
  focusScope = doc;
};

/** An in-page link: its `href` attribute is a fragment of this page. */
export const HASH_LINK_SELECTOR = "a[href^='#']";

/**
 * The element a fragment (`#size-guide`, `#taille%20guide`) names in `doc`, or null for a bare
 * `#`, a malformed escape, an unknown id or a document that cannot look an id up.
 */
const fragmentTarget = (doc, hash) => {
  if (typeof hash !== 'string' || hash.length < 2 || !hash.startsWith('#')) return null;
  if (!doc || typeof doc.getElementById !== 'function') return null;
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    return null;
  }
  return doc.getElementById(id);
};

/**
 * Show, at rest, every hidden reveal item that holds the fragment's target (`revealFocused`), and
 * every gated unit inside it: `#collection-grid` names the grid band's column, which holds the
 * band's own Ranges row, gated as a step of its own (loop 1, grid GAP 57).
 */
const revealFragment = (doc, hash) => {
  const target = fragmentTarget(doc, hash);
  let shown = revealFocused(target);
  (elementsUnder(target, GATE_SELECTOR) ?? []).forEach((unit) => {
    if (releaseGated(unit)) shown += 1;
  });
  return shown;
};

/** The document the in-page link listeners are on, so they are registered once per page. */
let hashScope = null;

/**
 * Once the page arms, show an in-page link's target at rest BEFORE the browser scrolls to it.
 * The scroll position is computed while the target's item still carries its entrance translate,
 * so "Which size?" landed its heading 32px too high (the offset only held under reduced motion).
 * A capturing `click` runs ahead of the browser's default scroll; `hashchange` covers history
 * steps and a script that sets the fragment; `initReveal` checks the URL's own fragment at arm
 * time, for a page opened on one.
 */
const listenForHash = (doc) => {
  if (!doc || doc === hashScope || typeof doc.addEventListener !== 'function') return;
  doc.addEventListener(
    'click',
    (event) => {
      const target = event?.target;
      const link =
        typeof target?.closest === 'function' ? target.closest(HASH_LINK_SELECTOR) : null;
      if (link) revealFragment(doc, link.getAttribute('href'));
    },
    true,
  );
  const scope = view();
  if (scope && typeof scope.addEventListener === 'function') {
    scope.addEventListener('hashchange', () => revealFragment(doc, view()?.location?.hash));
  }
  hashScope = doc;
};

/**
 * Reveal every `[data-pz-reveal]` item under `root` as it scrolls into view.
 *
 * With motion wanted it shows the items already on screen at once (no blink), arms the page with
 * `:root.pz-reveal-on` (and one `beforeprint` listener that runs `revealAll`, one capturing
 * `focusin` listener on the document that runs `revealFocused`, and the in-page link listeners
 * that show a fragment's target before the browser scrolls to it), then observes the rest: ONE
 * shared observer for plain and `item` blocks, one per `enter` block and per block taller than
 * the visible viewport (the enter threshold: the shared one would be out of its reach), one
 * shared observer for `fan` blocks at 0.3. It then shows the URL fragment's target. Without
 * it (no observer, reduced motion, the Theme Editor) it adds `pz-in` to every item and hides
 * nothing, because the page is never armed.
 *
 * @param {ParentNode|null} [root] Defaults to the document; `shopify:section:load` passes the
 *   section that was just re-rendered.
 * On a collection page (`collection`, read from the document) it takes the drawings' numbers
 * (`revealProfile`) and marks no split row: nothing enters from a side there.
 *
 * @param {{ doc?: Document, hasObserver?: boolean, reducedMotion?: boolean, designMode?: boolean,
 *   viewportHeight?: number, collection?: boolean }} [env] Test seams; each defaults to the real
 *   browser value.
 * @returns {number} How many items this call took responsibility for.
 */
export const initReveal = (root = globalThis.document, env = {}) => {
  const blocks = elementsUnder(root, REVEAL_SELECTOR);
  if (blocks === null) return 0;

  const fresh = blocks
    .filter((block) => ruleOf(block) !== REVEAL_OPT_OUT)
    .filter((block) => !revealed.has(block));
  fresh.forEach((block) => revealed.add(block));
  if (fresh.length === 0) return 0;

  const {
    doc = globalThis.document,
    hasObserver = typeof IntersectionObserver === 'function',
    reducedMotion = prefersReducedMotion(),
    designMode = isDesignMode(),
    viewportHeight = view()?.innerHeight ?? 0,
    collection = isCollectionPage(doc),
  } = env;

  if (!shouldArmReveal({ hasObserver, reducedMotion, designMode })) {
    // Nothing was ever hidden (the page is not armed); the class keeps the harness's settle check.
    fresh.forEach((block) => block.classList.add(REVEAL_CLASS));
    return fresh.length;
  }

  const profile = revealProfile(collection);

  /*
   * Every read first, then every write (PERF-07, PURRZ performance plan 2026-10-28): a position or
   * a height read after a class or attribute write makes the browser lay the page out again before
   * it can answer, so measuring and marking block by block cost one full layout per block. The
   * split rows' sides, which blocks are on screen and the waiting blocks' heights are all read
   * before anything is marked. Nothing is hidden before the page arms, so on start-up every value
   * is the one the old order read.
   */
  const rowSides = collection ? [] : splitRowSides(root, new Set(fresh));
  const shown = fresh.map((block) => onScreen(block, viewportHeight, profile.onScreen));
  const waiting = fresh.filter((_, index) => !shown[index]);
  /* A fan keeps its own threshold whatever its height, so it is never measured. */
  const heights = waiting.map((block) => (ruleOf(block) === REVEAL_FAN ? 0 : block.offsetHeight));

  rowSides.forEach(([child, side]) => child.setAttribute(REVEAL_FROM_ATTRIBUTE, side));
  // Painted visible before this module ran: shown without a transition, BEFORE the page arms.
  fresh.forEach((block, index) => {
    if (shown[index]) block.classList.add(REVEAL_CLASS);
  });

  const rootElement = doc?.documentElement;
  if (rootElement && !rootElement.classList.contains?.(REVEAL_ARMED_CLASS)) {
    rootElement.classList.add(REVEAL_ARMED_CLASS);
  }
  listenForPrint(doc);
  listenForFocus(doc);
  listenForHash(doc);

  const shared = [];
  const fans = [];
  waiting.forEach((block, index) => {
    const rule = ruleOf(block);
    const height = heights[index];
    if (rule === REVEAL_FAN) {
      fans.push(block);
    } else if (rule === REVEAL_ENTER || tallerThanView(height, viewportHeight)) {
      // Its own observer: the enter threshold, or the shared 0.12 it could never reach.
      const threshold = revealThreshold(rule, height, viewportHeight);
      observeBlocks([block], { threshold, rootMargin: REVEAL_ROOT_MARGIN }, { profile });
    } else {
      shared.push(block);
    }
  });
  if (shared.length > 0) {
    observeBlocks(
      shared,
      { threshold: REVEAL_THRESHOLD, rootMargin: REVEAL_ROOT_MARGIN },
      { profile },
    );
  }
  if (fans.length > 0) observeBlocks(fans, { threshold: FAN_THRESHOLD }, { fan: true });

  /* Opened on a fragment: its target at rest, once the observers exist to stop waiting on it. */
  revealFragment(doc, view()?.location?.hash);

  return fresh.length;
};

/* ---- The band gate (collection pages) ------------------------------------------------------- */

/**
 * The collection drawings' page gate (their `gate()`, `gFlush()` and `gShow()`): every top-level
 * band below the hero waits at opacity 0, 48px low, until the first scroll, wheel or touchmove.
 * Then each band whose top is within 95% of the viewport shows, 90ms after the one before (three
 * steps at most), and every other band shows, with no delay, once 8% of it is in view; over .8s
 * (opacity) and .9s (transform) on cubic-bezier(.23,1,.32,1). The hidden state is the layer's
 * `:root.pz-gate-on [data-pz-gate]:not(.pz-gate-in)`. The grid band's Ranges row is a step of its
 * own, as the drawings' separate Ranges section is (`GATE_LEAD_SELECTOR`, loop 1).
 */
export const GATE_ATTRIBUTE = 'data-pz-gate';
export const GATE_ARMED_CLASS = 'pz-gate-on';
export const GATE_SHOWN_CLASS = 'pz-gate-in';
export const GATE_ACTIVE_CLASS = 'pz-gating';
export const GATE_BAND_SELECTOR = '.pz-band';
export const GATE_SKIP_CLASS = 'pz-collection-hero';
export const GATE_THRESHOLD = 0.08;
export const GATE_STEP_MS = 90;
export const GATE_STEP_CAP = 3;
export const GATE_FLUSH_SHARE = 0.95;
/** The longer of the two transitions (`--pz-duration-gate-rise`); the clean-up waits this long. */
export const GATE_DURATION_MS = 900;

/**
 * Dispatched (bubbling) on a band the moment the gate shows it, so a band module can start its
 * first-view motion then. A band shown at rest (focus, a fragment, print, the failsafe) or never
 * gated (no JavaScript, reduced motion, the editor, any other page) hears nothing.
 */
export const GATE_EVENT = 'wf:band-in';

const GATE_SELECTOR = `[${GATE_ATTRIBUTE}]`;
const GATE_DELAY_PROPERTY = '--pz-gate-delay';
const GATE_SETTLE_MS = 100;
const FIRST_SCROLL_EVENTS = ['scroll', 'wheel', 'touchmove'];

/** Bands already gated, so a second call never gates one twice. */
const gated = new WeakSet();

/** Gated bands still waiting, each with the call that stops its wait (shows it at rest). */
const gateWaits = new WeakMap();

/**
 * The delays of `count` bands shown by one first scroll: 90ms apart, three steps at most.
 *
 * @param {number} count
 * @returns {number[]}
 */
export const gateDelays = (count) =>
  Array.from(
    { length: Math.max(0, count) },
    (_, index) => Math.min(index, GATE_STEP_CAP) * GATE_STEP_MS,
  );

/**
 * The threshold a band is observed at: the drawings' 0.08 whenever 8% of the band fits on one
 * screen, so a band taller than the screen still waits for 8% OF ITSELF, as drawn (loop 1: custom
 * GAP 52, lower GAP 1, size-toy S36, T26). Only a band taller than 12.5 screens, which could never
 * show 8% of itself at once, waits for 8% of the screen instead (72px at 900): a 30,000px grid (59
 * cards on a phone) would otherwise stay blank, the drawings' own defect, not copied.
 *
 * @param {number} height The band's laid-out height in pixels.
 * @param {number} viewportHeight
 * @returns {number}
 */
export const gateThreshold = (height, viewportHeight) => {
  if (!(height > 0) || !(viewportHeight > 0)) return GATE_THRESHOLD;
  if (height * GATE_THRESHOLD <= viewportHeight) return GATE_THRESHOLD;
  return (viewportHeight * GATE_THRESHOLD) / height;
};

/*
 * THE RANGES ROW, A GATE STEP OF ITS OWN (loop 1: grid GAP 57, bed NEW 5, lower GAP 1 (b)). The
 * drawings gate their Ranges row as a section of its own (`padding: 28px 32px 0`) above the product
 * grid section (`padding: 40px 32px 64px`); the build draws both in one grid band, the row
 * (`.pz-collection-grid__ranges`) 28px under the band top and 40px over the filter bar. Such a band
 * is gated as the drawing's two sections: the row (`data-pz-gate="lead"`), then the rest of the band
 * (the band itself, `data-pz-gate="split"`, which the layer never hides whole: it hides every
 * `[data-pz-gate-part]`, the pieces beside the row). Each waits for 8% of its own drawn region (the
 * row: band top to row bottom; the rest: row bottom to band bottom), and a first scroll that finds
 * both on screen shows the row, the rest 90ms later and the next band 180ms later, as drawn.
 */
export const GATE_LEAD_SELECTOR = '.pz-collection-grid__ranges';
export const GATE_LEAD = 'lead';
export const GATE_SPLIT = 'split';
export const GATE_PART_ATTRIBUTE = 'data-pz-gate-part';

/** The element children of `element`, as an array (none for a node without any). */
const childrenOf = (element) => [...(element?.children ?? [])];

/**
 * The rest of a split band: every element beside the path from the band down to its lead.
 * A piece may be an item reveal of its own (the filter bar): the layer moves a part with
 * `translate` and fades it with `filter: opacity()`, which compose with the reveal's `transform`
 * and `opacity`, as the drawing's gated section and its revealed bar do, so the bar's own border
 * rises with its contents.
 *
 * It walks the tree and writes nothing: `initGate` marks the parts `data-pz-gate-part` once every
 * band is measured (PERF-07, PURRZ performance plan 2026-10-28).
 *
 * @returns {Element[]} The parts.
 */
const partsOf = (band, lead) => {
  const parts = [];
  for (let node = lead; node && node !== band; node = node.parentElement ?? null) {
    const parent = node.parentElement ?? null;
    if (!parent) break;
    childrenOf(parent).forEach((child) => {
      if (child === node || typeof child?.setAttribute !== 'function') return;
      parts.push(child);
    });
  }
  return parts;
};

/** A `rootMargin` that extends the viewport `px` below its bottom edge. */
const belowMargin = (px) => `0px 0px ${Math.max(0, Math.round(px))}px 0px`;

/** The share of `height` that makes `amount` px, kept to an observer's 0..1. */
const shareOf = (amount, height) => Math.min(1, Math.max(0, amount / height));

/**
 * The gate units of one band: the band alone, or, for a band that holds a lead, the lead and then
 * the rest of the band. Each says which element carries the gate, what is observed at what
 * threshold and margin, where its drawn region lies (for the first-scroll flush), and which pieces
 * it marks `data-pz-gate-part` (only the rest of a split band has any). It reads and never writes,
 * so `initGate` can measure every band before it marks one (PERF-07).
 *
 * An observer reads its target from the viewport extended `margin` px below, so the lead (28px
 * under the band top) is seen as the drawn row section from the band top, and the rest is read on
 * its tallest piece (the results), seen from the row bottom: the drawn product grid section. A
 * region is read off the piece that moves, so while it waits it sits 48px low, as the drawings'
 * hidden section does when their first-scroll flush reads it.
 */
const unitsOf = (band, viewportHeight) => {
  const whole = {
    element: band,
    value: '',
    target: band,
    threshold: gateThreshold(band.offsetHeight, viewportHeight),
    margin: null,
    region: () => rectOf(band),
    parts: [],
  };
  const lead =
    typeof band.querySelector === 'function' ? band.querySelector(GATE_LEAD_SELECTOR) : null;
  const bandRect = lead ? rectOf(band) : null;
  const leadRect = lead ? rectOf(lead) : null;
  if (!lead || !bandRect || !leadRect || !(lead.offsetHeight > 0)) return [whole];

  const parts = partsOf(band, lead);
  const above = leadRect.top - bandRect.top;
  const rowRegion = leadRect.bottom - bandRect.top;
  const leadUnit = {
    element: lead,
    value: GATE_LEAD,
    target: lead,
    threshold: shareOf(gateThreshold(rowRegion, viewportHeight) * rowRegion, lead.offsetHeight),
    margin: belowMargin(above),
    region: () => {
      const rect = rectOf(lead);
      return rect ? { top: rect.top - above, bottom: rect.bottom } : null;
    },
    parts: [],
  };

  const body = parts.reduce(
    (tallest, part) => ((part.offsetHeight ?? 0) > (tallest?.offsetHeight ?? 0) ? part : tallest),
    null,
  );
  const bodyRect = body ? rectOf(body) : null;
  const restRegion = bandRect.bottom - leadRect.bottom;
  const reads = Boolean(body && bodyRect && body.offsetHeight > 0);
  const over = reads ? bodyRect.top - leadRect.bottom : 0;
  const restUnit = {
    element: band,
    value: GATE_SPLIT,
    parts,
    region: () => {
      const rect = reads ? rectOf(body) : rectOf(lead);
      const bottom = rectOf(band)?.bottom ?? 0;
      if (!rect) return null;
      return { top: reads ? rect.top - over : rect.bottom, bottom };
    },
    ...(reads
      ? {
          target: body,
          threshold: shareOf(
            gateThreshold(restRegion, viewportHeight) * restRegion,
            body.offsetHeight,
          ),
          margin: belowMargin(over),
        }
      : { target: band, threshold: whole.threshold, margin: null }),
  };
  return [leadUnit, restUnit];
};

/**
 * Show a gated band (or a band's lead) at rest (`pz-gate-in` without `pz-gating`); true when it
 * was waiting.
 */
const releaseGated = (band) => {
  if (typeof band?.getAttribute !== 'function' || band.getAttribute(GATE_ATTRIBUTE) === null) {
    return false;
  }
  if (band.classList?.contains?.(GATE_SHOWN_CLASS)) return false;
  const stopWaiting = gateWaits.get(band);
  gateWaits.delete(band);
  stopWaiting?.();
  band.classList?.add?.(GATE_SHOWN_CLASS);
  return true;
};

/** Tell a band it is in (`GATE_EVENT`, bubbling), through the window's CustomEvent. */
const announce = (band) => {
  const BandEvent = view()?.CustomEvent;
  if (typeof BandEvent !== 'function' || typeof band.dispatchEvent !== 'function') return;
  band.dispatchEvent(new BandEvent(GATE_EVENT, { bubbles: true }));
};

/** The top-level bands of the page's <main> (else of `root`), the hero left out. */
const gateBands = (root) => {
  const main = typeof root?.querySelector === 'function' ? root.querySelector('main') : null;
  const bands = elementsUnder(main ?? root, GATE_BAND_SELECTOR) ?? [];
  return bands.filter(
    (band) =>
      !band.classList?.contains?.(GATE_SKIP_CLASS) &&
      !band.parentElement?.closest?.(GATE_BAND_SELECTOR),
  );
};

/**
 * Gate the bands of a collection page (lower GAP 1, custom GAP 52).
 *
 * Off a collection page, without an IntersectionObserver, under reduced motion or in the Theme
 * Editor it does nothing at all, so nothing is hidden. Otherwise it measures every band below the
 * hero, then marks each `data-pz-gate` (a band holding a Ranges row: the row `lead`, the band
 * `split` and its other pieces `data-pz-gate-part`, see `unitsOf`), THEN arms the page
 * (`pz-gate-on`), and observes each unit at 0.08 of its drawn region (`gateThreshold`). A page
 * that opens already scrolled (a reload, a fragment) counts as scrolled: the units on screen are
 * shown at rest and the rest show as they come into view. Focus inside a band, an in-page link to
 * it and print show it at rest (`revealFocused`, `revealAll`); so does the failsafe, when no
 * observer ever calls back.
 *
 * @param {ParentNode|null} [root]
 * @param {{ doc?: Document, hasObserver?: boolean, reducedMotion?: boolean, designMode?: boolean,
 *   collection?: boolean, viewportHeight?: number, scrollY?: number }} [env] Test seams; each
 *   defaults to the real browser value.
 * @returns {number} How many bands this call took responsibility for.
 */
export const initGate = (root = globalThis.document, env = {}) => {
  const {
    doc = globalThis.document,
    hasObserver = typeof IntersectionObserver === 'function',
    reducedMotion = prefersReducedMotion(),
    designMode = isDesignMode(),
    collection = isCollectionPage(doc),
    viewportHeight = view()?.innerHeight ?? 0,
    scrollY = view()?.scrollY ?? 0,
  } = env;
  if (!collection || !shouldArmReveal({ hasObserver, reducedMotion, designMode })) return 0;

  const bands = gateBands(root).filter((band) => !gated.has(band));
  if (bands.length === 0) return 0;
  bands.forEach((band) => gated.add(band));

  const scope = view();
  let scrolled = scrollY > 0;
  let failsafe = null;
  /* The units still waiting, by the element that carries the gate, in page order. */
  const pending = new Map();
  /* One observer per threshold and margin: the drawings' 0.08 shared, any other unit's own. */
  const observers = new Map();
  const watching = new Map();
  const byTarget = new Map();

  const onEntries = (entries) => {
    cancel(failsafe);
    failsafe = null;
    if (!scrolled) return;
    entries.forEach((entry) => {
      const element = byTarget.get(entry.target);
      if (entry.isIntersecting && pending.has(element)) show(element, 0);
    });
  };

  const watch = ({ element, target, threshold, margin }) => {
    const key = `${threshold}|${margin ?? ''}`;
    if (!observers.has(key)) {
      const options = margin === null ? { threshold } : { threshold, rootMargin: margin };
      observers.set(key, new IntersectionObserver(onEntries, options));
    }
    const observer = observers.get(key);
    watching.set(element, { observer, target });
    byTarget.set(target, element);
    observer.observe(target);
  };

  const stop = (element) => {
    pending.delete(element);
    gateWaits.delete(element);
    const watched = watching.get(element);
    watched?.observer.unobserve(watched.target);
    watching.delete(element);
  };

  /* Called only once the observers and the first-scroll listener exist, below. */
  const show = (element, delay) => {
    stop(element);
    element.style?.setProperty?.(GATE_DELAY_PROPERTY, `${delay}ms`);
    element.classList.add(GATE_ACTIVE_CLASS);
    element.classList.add(GATE_SHOWN_CLASS);
    /* A band is told it is in; a band's lead is a piece of it, not a band. */
    if (element.getAttribute?.(GATE_ATTRIBUTE) !== GATE_LEAD) announce(element);
    later(
      () => {
        element.classList.remove(GATE_ACTIVE_CLASS);
        element.style?.removeProperty?.(GATE_DELAY_PROPERTY);
      },
      GATE_DURATION_MS + delay + GATE_SETTLE_MS,
    );
  };

  const flush = () => {
    let step = 0;
    [...pending.values()].forEach((unit) => {
      const region = unit.region();
      if (region && region.top < viewportHeight * GATE_FLUSH_SHARE) {
        show(unit.element, Math.min(step, GATE_STEP_CAP) * GATE_STEP_MS);
        step += 1;
      }
    });
  };

  const onFirstScroll = () => {
    if (scrolled) return;
    scrolled = true;
    FIRST_SCROLL_EVENTS.forEach((type) =>
      scope?.removeEventListener?.(type, onFirstScroll, { passive: true }),
    );
    flush();
  };

  /*
   * Every read first, then every write (PERF-07, PURRZ performance plan 2026-10-28): marking a
   * band's pieces while it and the bands after it were still being measured made each read after
   * that lay the page out again. So every band's units, and on a page opened scrolled every unit's
   * region, are read before the first piece is marked. Nothing is hidden before the page arms, so
   * on start-up every value is the one the old order read.
   */
  const units = bands.flatMap((band) => unitsOf(band, viewportHeight));
  const regions = units.map((unit) => (scrolled ? unit.region() : null));

  // Marked first, armed after: the hidden state needs both, so nothing blinks out of order.
  units.forEach((unit, index) => {
    const { element } = unit;
    unit.parts.forEach((part) => part.setAttribute(GATE_PART_ATTRIBUTE, ''));
    element.setAttribute(GATE_ATTRIBUTE, unit.value);
    const region = regions[index];
    if (region && region.top < viewportHeight && region.bottom > 0) {
      element.classList.add(GATE_SHOWN_CLASS);
      return;
    }
    pending.set(element, unit);
    gateWaits.set(element, () => stop(element));
  });
  const rootElement = doc?.documentElement;
  if (rootElement && !rootElement.classList.contains?.(GATE_ARMED_CLASS)) {
    rootElement.classList.add(GATE_ARMED_CLASS);
  }
  listenForPrint(doc);
  listenForFocus(doc);
  listenForHash(doc);

  if (pending.size === 0) return bands.length;
  pending.forEach(watch);
  failsafe = later(() => {
    failsafe = null;
    [...pending.keys()].forEach((element) => releaseGated(element));
    observers.forEach((observer) => observer.disconnect());
  }, REVEAL_FAILSAFE_MS);
  if (!scrolled && typeof scope?.addEventListener === 'function') {
    FIRST_SCROLL_EVENTS.forEach((type) =>
      scope.addEventListener(type, onFirstScroll, { passive: true }),
    );
  }

  /* Opened on a fragment: the band that holds its target at rest, as the reveal does. */
  revealFragment(doc, view()?.location?.hash);
  return bands.length;
};

/* ---- The pop (collection pages) ----------------------------------------------------------- */

/**
 * The collection drawings' count and badge pop (`bfPop` / `bfPopB`): the layer plays `pz-pop-a` or
 * `pz-pop-b` (scale 1, 1.08 at 40%, 1; .4s ease, or the timing the element's own rule sets) by the
 * value of `data-pz-pop`, so alternating the value restarts the pop on every change.
 */
export const POP_ATTRIBUTE = 'data-pz-pop';

/**
 * The next pop name: `a` first, then `b`, then `a`.
 *
 * @param {string|null} current
 * @returns {'a'|'b'}
 */
export const nextPop = (current) => (current === 'a' ? 'b' : 'a');

/**
 * Pop `element` once (handed to band modules as `api.pop`). Under reduced motion it pops nothing
 * and takes off a pop already written.
 *
 * @param {Element|null} element
 * @param {{ reducedMotion?: boolean }} [options] Test seam; defaults to the reader's preference.
 * @returns {'a'|'b'|null} The name it wrote, or null.
 */
export const pop = (element, { reducedMotion = prefersReducedMotion() } = {}) => {
  if (typeof element?.setAttribute !== 'function' || typeof element.getAttribute !== 'function') {
    return null;
  }
  if (reducedMotion) {
    element.removeAttribute?.(POP_ATTRIBUTE);
    return null;
  }
  const value = nextPop(element.getAttribute(POP_ATTRIBUTE));
  element.setAttribute(POP_ATTRIBUTE, value);
  return value;
};

/* ---- Rails ---------------------------------------------------------------------------------- */

/*
 * A rail is a `.pz-rail` row, or any scroller that carries the bare `data-pz-rail` hook: the
 * product page's compare table cannot take `.pz-rail` (which forces `display: flex`) and still
 * needs the cut-edge signal and keyboard reach (howto brief 0.2 E; the mask rules in
 * `purrz-design.css` read `:is(.pz-rail, [data-pz-rail])`).
 */
export const RAIL_SELECTOR = '.pz-rail, [data-pz-rail]';
export const RAIL_EDGE_ATTRIBUTE = 'data-pz-edge';

/**
 * A finder's carousel carries `.pz-rail` for its row, but the collection drawings cut it clean at
 * the panel with no fade and give it no tab stop (fidelity FX0, bed GAP 10-11, size-toy S10): a
 * rail with this class is never bound here.
 */
export const RAIL_PLAIN_CLASS = 'pz-finder__rail';

/**
 * Marks a `tabindex="0"` this file added, so it can take back exactly that one when the rail stops
 * overflowing. An author's tabindex never carries it and is never touched.
 */
export const RAIL_FOCUS_ATTRIBUTE = 'data-pz-rail-focus';

/** Sub-pixel scroll positions and zoom make an exact comparison against 0 unreliable. */
const RAIL_EPSILON = 2;

/**
 * Which end of a rail is cut, for the mask in `purrz-design.css`.
 *
 * A hidden scrollbar removes the only hint a mouse user had that a row continues, so a rail that
 * overflows says so with a 40px fade at the end that is cut. An empty string means it does not
 * overflow and carries no attribute at all.
 *
 * @param {number} scrollLeft
 * @param {number} scrollWidth
 * @param {number} clientWidth
 * @returns {''|'start'|'end'|'both'}
 */
export const railEdge = (scrollLeft, scrollWidth, clientWidth) => {
  if (!(scrollWidth - clientWidth > RAIL_EPSILON)) return '';
  const atStart = scrollLeft <= RAIL_EPSILON;
  const atEnd = scrollLeft + clientWidth >= scrollWidth - RAIL_EPSILON;
  if (atStart) return 'end';
  if (atEnd) return 'start';
  return 'both';
};

/*
 * THE RAILS ONE SHARED `resize` LISTENER SERVES.
 *
 * A listener per rail would be a leak with a stopwatch on it. `shopify:section:load` replaces a
 * section's markup wholesale on every edit in the Theme Editor, and each replaced rail would
 * leave its `update` closure - and the detached element that closure holds - registered on
 * `window` for the rest of the session. `railsBound` cannot help: the re-rendered section is new
 * elements. So one listener walks this list instead, dropping on the way past whatever has left
 * the document.
 */
const boundRails = [];

/** The window the shared listener is on, so it is registered once per page rather than per rail. */
let resizeScope = null;

const dropDetachedRails = () => {
  for (let index = boundRails.length - 1; index >= 0; index -= 1) {
    const entry = boundRails[index];
    /* Strictly false: `isConnected` is undefined on a test double, which is not "detached". */
    if (entry.rail.isConnected === false) {
      /* Let the observer go of it too, so a Theme Editor re-render leaks nothing. */
      entry.watched.forEach((element) => entry.observer?.unobserve(element));
      boundRails.splice(index, 1);
    }
  }
};

const updateBoundRails = () => {
  dropDetachedRails();
  boundRails.forEach((entry) => entry.update());
};

const listenForResize = () => {
  const scope = view();
  if (!scope || scope === resizeScope || typeof scope.addEventListener !== 'function') return;
  scope.addEventListener('resize', updateBoundRails, { passive: true });
  resizeScope = scope;
};

/*
 * A rail's scrollWidth also changes with no window resize: the web font swapping in (the compare
 * table is 934px wide in the fallback font and 946px in Poppins, so at 986 to 1009px it overflows
 * only after the swap), or content loading. ONE ResizeObserver, created the first time a rail is
 * bound, watches every rail and its first child and re-runs the shared update; `document.fonts`
 * covers a browser without ResizeObserver. Both are reached through the window and skipped where
 * they do not exist.
 */
let railObserver = null;

/** The window the shared observer was made for, so it is created once per page. */
let railObserverScope = null;

/** The `document.fonts` the font listeners are on, so they are registered once per page. */
let fontsScope = null;

const sharedRailObserver = () => {
  const scope = view();
  if (!scope) return null;
  if (scope !== railObserverScope) {
    railObserverScope = scope;
    railObserver =
      typeof scope.ResizeObserver === 'function'
        ? new scope.ResizeObserver(updateBoundRails)
        : null;
  }
  return railObserver;
};

/** Watch `rail` and its first child; returns the observer and what it watches, for the drop. */
const observeRail = (rail) => {
  const observer = sharedRailObserver();
  if (observer === null) return { observer: null, watched: [] };
  const watched = [rail, rail.firstElementChild].filter(Boolean);
  watched.forEach((element) => observer.observe(element));
  return { observer, watched };
};

const listenForFonts = () => {
  const fonts = view()?.document?.fonts;
  if (!fonts || fonts === fontsScope) return;
  fontsScope = fonts;
  fonts.ready?.then?.(updateBoundRails, () => undefined);
  if (typeof fonts.addEventListener === 'function') {
    fonts.addEventListener('loadingdone', updateBoundRails);
  }
};

/**
 * Give every `.pz-rail` under `root` its cut-edge signal, and make an overflowing one reachable
 * by keyboard - a scroll container that answers only to a pointer is a trap for everyone else.
 * A rail that stops overflowing loses the tab stop again, but only the one this file added
 * (`data-pz-rail-focus`), and only once focus has left it. Every rail is re-measured on a window
 * resize, when its own box or its first child's changes size, and when a web font loads.
 *
 * @param {ParentNode|null} [root]
 * @returns {number} How many rails this call bound.
 */
export const initRails = (root = globalThis.document) => {
  const rails = elementsUnder(root, RAIL_SELECTOR);
  if (rails === null) return 0;

  const fresh = rails
    .filter((rail) => !rail.classList?.contains?.(RAIL_PLAIN_CLASS))
    .filter((rail) => !railsBound.has(rail));
  fresh.forEach((rail) => railsBound.add(rail));
  dropDetachedRails();

  fresh.forEach((rail) => {
    const update = () => {
      const edge = railEdge(rail.scrollLeft, rail.scrollWidth, rail.clientWidth);
      if (edge === '') {
        rail.removeAttribute(RAIL_EDGE_ATTRIBUTE);
        /* Focused: keep the stop, or focus drops to the body; the next update after blur. */
        const focused = rail.ownerDocument?.activeElement === rail;
        if (rail.hasAttribute(RAIL_FOCUS_ATTRIBUTE) && !focused) {
          rail.removeAttribute('tabindex');
          rail.removeAttribute(RAIL_FOCUS_ATTRIBUTE);
        }
        return;
      }
      rail.setAttribute(RAIL_EDGE_ATTRIBUTE, edge);
      if (!rail.hasAttribute('tabindex')) {
        rail.setAttribute('tabindex', '0');
        rail.setAttribute(RAIL_FOCUS_ATTRIBUTE, '');
      }
    };

    /* The scroll listener is on the rail itself, so it is collected with the rail. */
    rail.addEventListener('scroll', update, { passive: true });
    boundRails.push({ rail, update, ...observeRail(rail) });
    update();
  });

  if (fresh.length > 0) {
    listenForResize();
    listenForFonts();
  }

  return fresh.length;
};

/* ---- Card text ------------------------------------------------------------------------------ */

/*
 * THE COMPACT CARD'S THREE LINES (owner request 2026-10-11: "if the product title is 2 lines, make
 * the information of it 1 line only and add ...; for the 1 line product title it should be 2 lines
 * ... so that there will be no space"). On a phone the compact collection card's name and
 * description share three lines, so no card in a row is left with a gap under its text. CSS cannot
 * count the lines a heading wraps to, so every collection card is marked with its name's line count
 * (`data-pz-name-lines`), and the layer's phone block cuts the description to one line under a
 * two-line name. Without this file the description keeps two lines: nothing is hidden that the
 * markup does not still hold. One shared ResizeObserver on the names re-measures a card on a width
 * change or a web font swap, and lets go of a name that has left the document.
 */
export const CARD_TEXT_SELECTOR = '.pz-product-card--collection';
export const CARD_NAME_SELECTOR = '.pz-product-card__title';
export const NAME_LINES_ATTRIBUTE = 'data-pz-name-lines';

/**
 * How many lines a name wraps to: its box height over its line height, rounded, at least one.
 * Zero when there is nothing to count (no box, or a line height that is not a length).
 *
 * @param {number} height
 * @param {number} lineHeight
 * @returns {number}
 */
export const nameLines = (height, lineHeight) =>
  height > 0 && lineHeight > 0 ? Math.max(1, Math.round(height / lineHeight)) : 0;

const cardsBound = new WeakSet();

/** The card each observed name belongs to. */
const cardOfName = new WeakMap();

/** Every read first, then every write: one layout for a whole grid, not one per card. */
const markNames = (names) => {
  const counts = names.map((name) =>
    nameLines(
      rectOf(name)?.height ?? 0,
      Number.parseFloat(view()?.getComputedStyle?.(name)?.lineHeight ?? ''),
    ),
  );
  names.forEach((name, index) => {
    const card = cardOfName.get(name);
    if (!card) return;
    if (counts[index] > 0) card.setAttribute(NAME_LINES_ATTRIBUTE, String(counts[index]));
    else card.removeAttribute(NAME_LINES_ATTRIBUTE);
  });
};

let nameObserver = null;

/** The window the shared observer was made for, so it is created once per page. */
let nameObserverScope = null;

const sharedNameObserver = () => {
  const scope = view();
  if (!scope) return null;
  if (scope !== nameObserverScope) {
    nameObserverScope = scope;
    nameObserver =
      typeof scope.ResizeObserver === 'function'
        ? new scope.ResizeObserver((entries) => {
            const names = [];
            entries.forEach(({ target }) => {
              /* Strictly false: `isConnected` is undefined on a test double. */
              if (target.isConnected === false) nameObserver?.unobserve(target);
              else names.push(target);
            });
            markNames(names);
          })
        : null;
  }
  return nameObserver;
};

/**
 * Mark every collection card under `root` with its name's line count. With a ResizeObserver the
 * first measure is the observer's first call, after the browser's own layout; without one each
 * card is measured as it is bound.
 *
 * @param {ParentNode|null} [root]
 * @returns {number} How many cards this call bound.
 */
export const initCardText = (root = globalThis.document) => {
  const cards = elementsUnder(root, CARD_TEXT_SELECTOR);
  if (cards === null) return 0;

  const names = [];
  cards.forEach((card) => {
    if (cardsBound.has(card)) return;
    const name = card.querySelector?.(CARD_NAME_SELECTOR);
    if (!name) return;
    cardsBound.add(card);
    cardOfName.set(name, card);
    names.push(name);
  });

  const observer = sharedNameObserver();
  if (observer) names.forEach((name) => observer.observe(name));
  else markNames(names);
  return names.length;
};

/* ---- Module loader -------------------------------------------------------------------------- */

/*
 * A band with behaviour carries `data-pz-module="{{ 'purrz-<name>.js' | asset_url }}"` on its
 * `<section>`. `asset_url` renders a versioned, same-origin, CORS-open URL, so `import()` of it is
 * cached exactly as long as the file is unchanged. The band module exports
 * `init(section, api) => cleanup | undefined` and imports nothing itself: an unversioned relative
 * import would be cached for a year and would instantiate a second copy of any shared state, so
 * everything shared reaches it through `moduleApi`.
 */

export const MODULE_SELECTOR = '[data-pz-module]';
export const MODULE_ATTRIBUTE = 'data-pz-module';
export const MODULE_STATE_ATTRIBUTE = 'data-pz-module-state';
export const MODULE_LOAD_ATTRIBUTE = 'data-pz-module-load';

/** A band's module starts once the band is within one viewport of the screen. */
export const MODULE_NEAR_MARGIN = '100% 0px 100% 0px';

const MODULE_EAGER = 'eager';

/** One import per URL for the page's life: two instances of a band share one module. */
const modulePromises = new Map();

/**
 * Hosts taken, each with its entry `{ cleanup }`. A new object per start, so a module that
 * finishes loading after its host was destroyed (or restarted) can tell it was superseded.
 */
const moduleHosts = new WeakMap();

/** Hosts waiting for the near observer, with that observer, so an unload can stop the wait. */
const moduleWaiting = new WeakMap();

/** `import()` of a band module's versioned asset URL. */
export const defaultImporter = (url) => import(/* @vite-ignore */ url);

/**
 * The module namespace at `url`, imported once. A failure is forgotten, so the next start (a
 * Theme Editor section load, a later scroll) tries again.
 *
 * @param {string} url
 * @param {(url: string) => Promise<object>} [importer]
 * @returns {Promise<object>}
 */
export const loadModule = (url, importer = defaultImporter) => {
  if (!modulePromises.has(url)) {
    const pending = Promise.resolve().then(() => importer(url));
    pending.catch(() => {
      if (modulePromises.get(url) === pending) modulePromises.delete(url);
    });
    modulePromises.set(url, pending);
  }
  return modulePromises.get(url);
};

/** The helpers a band module receives, so no band module ever imports another file. */
export const moduleApi = Object.freeze({
  prefersReducedMotion,
  isDesignMode,
  initReveal,
  initRails,
  later,
  cancel,
  pop,
});

/**
 * Import `host`'s module and call its `init(host, api)`. Isolated: a 404, a syntax error, a module
 * without `init` or an `init` that throws marks only this host `error`, and no other band.
 *
 * @param {Element} host The element carrying `data-pz-module`.
 * @param {{ importer?: (url: string) => Promise<object>, api?: object }} [options]
 * @returns {Promise<boolean>} Whether `init` ran.
 */
export const startModule = (host, { importer = defaultImporter, api = moduleApi } = {}) => {
  const url = host?.getAttribute?.(MODULE_ATTRIBUTE) ?? '';
  if (url === '' || moduleHosts.has(host)) return Promise.resolve(false);

  const entry = { cleanup: undefined };
  moduleHosts.set(host, entry);
  host.setAttribute(MODULE_STATE_ATTRIBUTE, 'loading');

  return loadModule(url, importer)
    .then((namespace) => {
      if (typeof namespace?.init !== 'function') throw new TypeError(`${url} exports no init`);
      // Unloaded by the Theme Editor, or removed from the page, while the module loaded.
      if (moduleHosts.get(host) !== entry) return false;
      if (host.isConnected === false) {
        moduleHosts.delete(host);
        host.removeAttribute(MODULE_STATE_ATTRIBUTE);
        return false;
      }
      const cleanup = namespace.init(host, api);
      entry.cleanup = typeof cleanup === 'function' ? cleanup : undefined;
      host.setAttribute(MODULE_STATE_ATTRIBUTE, 'ready');
      return true;
    })
    .catch(() => {
      // Isolated: one band's failure never reaches another band.
      if (moduleHosts.get(host) !== entry) return false;
      moduleHosts.delete(host);
      host.setAttribute(MODULE_STATE_ATTRIBUTE, 'error');
      return false;
    });
};

/**
 * Start every `[data-pz-module]` host under `root` (root included): at once when it carries
 * `data-pz-module-load="eager"` (the chrome) or there is no IntersectionObserver, otherwise the
 * first time it comes within one viewport, through one observer shared by this call's hosts.
 *
 * @param {ParentNode|null} [root]
 * @param {{ importer?: (url: string) => Promise<object>, api?: object, hasObserver?: boolean }} [options]
 * @returns {number} How many hosts this call took.
 */
export const initModules = (root = globalThis.document, options = {}) => {
  const hosts = elementsUnder(root, MODULE_SELECTOR);
  if (hosts === null) return 0;

  const {
    importer = defaultImporter,
    api = moduleApi,
    hasObserver = typeof IntersectionObserver === 'function',
  } = options;
  const start = (host) => startModule(host, { importer, api });

  const fresh = hosts.filter((host) => !moduleHosts.has(host) && !moduleWaiting.has(host));
  const near = [];
  fresh.forEach((host) => {
    if (!hasObserver || host.getAttribute(MODULE_LOAD_ATTRIBUTE) === MODULE_EAGER) start(host);
    else near.push(host);
  });

  if (near.length > 0) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || moduleWaiting.get(entry.target) !== observer) return;
          moduleWaiting.delete(entry.target);
          observer.unobserve(entry.target);
          start(entry.target);
        });
      },
      { rootMargin: MODULE_NEAR_MARGIN },
    );
    near.forEach((host) => {
      moduleWaiting.set(host, observer);
      observer.observe(host);
    });
  }

  return fresh.length;
};

/**
 * Run the cleanup of every host under `root` (root included) and forget it, so the same element
 * could start again. A throwing cleanup is isolated. A host still waiting to come near stops
 * waiting.
 *
 * @param {ParentNode|null} root
 * @returns {number} How many hosts were released.
 */
export const destroyModules = (root) => {
  const hosts = elementsUnder(root, MODULE_SELECTOR);
  if (hosts === null) return 0;

  let released = 0;
  hosts.forEach((host) => {
    const observer = moduleWaiting.get(host);
    if (observer) {
      moduleWaiting.delete(host);
      observer.unobserve(host);
      released += 1;
    }

    const entry = moduleHosts.get(host);
    if (!entry) return;
    moduleHosts.delete(host);
    try {
      entry.cleanup?.();
    } catch {
      /* Isolated: one band's broken cleanup never stops another's. */
    }
    host.removeAttribute(MODULE_STATE_ATTRIBUTE);
    released += 1;
  });
  return released;
};

/* ---- Auto-start ----------------------------------------------------------------------------- */

/*
 * A module script is deferred, so by the time this runs the document is normally parsed; the
 * `loading` branch is there for the case it is not. `shopify:section:load` fires when a merchant
 * edits a section in the Theme Editor, which replaces its markup wholesale - the new items have
 * never been observed and the new band module never started; `shopify:section:unload` fires for
 * the old markup first, and its band module's cleanup runs.
 */
if (typeof document !== 'undefined') {
  const start = () => {
    initGate();
    initReveal();
    initRails();
    initCardText();
    initModules();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initGate(event.target);
    initReveal(event.target);
    initRails(event.target);
    initCardText(event.target);
    initModules(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyModules(event.target);
  });
}
